<template>
  <div id="app" class="app">
    <el-container class="layout">
      <el-header v-if="!hideNav" class="topbar" height="64px">
        <div class="topbar__inner">
          <div class="brand" role="banner" @click="onSelect('home')">
            <img class="brand__logo" :src="logoUrl" :alt="$t('app.brand')" />
            <div class="brand__text">
              <div class="brand__name">{{ $t('app.brand') }}</div>
              <div class="brand__tagline">PRANK GAME</div>
            </div>
          </div>

          <el-menu
            class="nav"
            mode="horizontal"
            :default-active="activeMenu"
            background-color="transparent"
            text-color="rgba(15,23,42,0.75)"
            active-text-color="#0f172a"
            @select="onSelect"
          >
            <el-menu-item index="home">{{ $t('nav.home') }}</el-menu-item>
            <el-menu-item index="games">{{ $t('nav.games') }}</el-menu-item>
            <el-menu-item v-if="isLoggedIn" index="orders">{{ $t('nav.orders') }}</el-menu-item>
            <el-menu-item index="recruit">{{ $t('nav.recruit') }}</el-menu-item>
            <el-menu-item index="partner">{{ $t('nav.partner') }}</el-menu-item>
            <el-menu-item index="faq">{{ $t('nav.faq') }}</el-menu-item>
          </el-menu>

          <div class="actions">
            <el-dropdown trigger="click" @command="onLangCommand">
              <span class="lang">
                {{ langLabel }}
                <i class="el-icon-arrow-down el-icon--right"></i>
              </span>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="zh-CN">{{ $t('common.langZh') }}</el-dropdown-item>
                <el-dropdown-item command="en-US">{{ $t('common.langEn') }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>

            <template v-if="isLoggedIn">
              <div class="walletRow">
                <div class="wallet" :title="$t('common.balance')">
                  <span class="wallet__label">{{ $t('common.balance') }}</span>
                  <span class="wallet__value">{{ formattedBalance }}</span>
                </div>
                <el-button size="small" type="primary" class="wallet-recharge" @click="openRechargeDialog">
                  {{ $t('nav.recharge') }}
                </el-button>
              </div>
              <el-dropdown trigger="click" @command="onUserCommand">
                <el-button size="small" plain class="btn--ghost user-btn">
                  {{ displayName }}
                  <i class="el-icon-arrow-down el-icon--right"></i>
                </el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="orders">{{ $t('nav.orders') }}</el-dropdown-item>
                  <el-dropdown-item command="coupons">{{ $t('nav.coupons') }}</el-dropdown-item>
                  <el-dropdown-item command="logout">{{ $t('common.logout') }}</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
            <template v-else>
              <el-button size="small" type="primary" class="btn--primary" @click="onLogin">
                {{ $t('common.login') }}
              </el-button>
            </template>
          </div>
        </div>
      </el-header>

      <el-main :class="['main', { 'main--auth': hideNav }]">
        <router-view />
      </el-main>

      <el-footer v-if="!hideNav" class="site-footer" height="auto">
        <div class="site-footer__inner">
          <a
            class="site-footer__beian"
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
          >
            浙ICP备2024086644号
          </a>
        </div>
      </el-footer>
    </el-container>

    <RechargeDialog ref="rechargeDialog" />
  </div>
</template>

<script>
import { setLocale } from './i18n'
import { sessionState, syncSession, refreshProfileFromServer, logoutUser, isLoggedIn as checkLoggedIn } from '@/utils/auth'
import RechargeDialog from '@/components/RechargeDialog.vue'
import logoUrl from '@/assets/logo.png'

export default {
  name: 'App',
  components: {
    RechargeDialog
  },
  data() {
    return {
      logoUrl
    }
  },
  computed: {
    hideNav() {
      return Boolean(this.$route && this.$route.meta && this.$route.meta.hideNav)
    },
    activeMenu() {
      const name = this.$route && this.$route.name
      return name || 'home'
    },
    langLabel() {
      return this.$i18n && this.$i18n.locale === 'en-US' ? 'EN' : '中文'
    },
    isLoggedIn() {
      void sessionState.user
      return checkLoggedIn()
    },
    displayName() {
      const u = sessionState.user
      return (u && u.username) || this.$t('common.guest')
    },
    formattedBalance() {
      const u = sessionState.user
      const b = u && typeof u.balance === 'number' ? u.balance : Number((u && u.balance) || 0)
      if (Number.isNaN(b)) return '¥0.00'
      return new Intl.NumberFormat(this.$i18n.locale === 'en-US' ? 'en-US' : 'zh-CN', {
        style: 'currency',
        currency: 'CNY'
      }).format(b)
    }
  },
  mounted() {
    syncSession()
    refreshProfileFromServer()
  },
  methods: {
    openRechargeDialog() {
      const dlg = this.$refs.rechargeDialog
      if (dlg && typeof dlg.open === 'function') {
        dlg.open()
      }
    },
    onSelect(key) {
      const map = {
        home: '/home',
        games: '/games',
        orders: '/orders',
        coupons: '/coupons',
        recruit: '/recruit',
        partner: '/partner',
        faq: '/faq'
      }
      const path = map[key] || '/home'
      if (this.$route && this.$route.path === path) return
      this.$router.push(path)
    },
    onLangCommand(command) {
      setLocale(this.$i18n, command)
    },
    onLogin() {
      if (this.isLoggedIn) {
        this.$router.push('/home')
        return
      }
      this.$router.push('/login')
    },
    onUserCommand(cmd) {
      if (cmd === 'orders') {
        this.$router.push('/orders')
        return
      }
      if (cmd === 'coupons') {
        this.$router.push('/coupons')
        return
      }
      if (cmd === 'logout') {
        logoutUser()
        this.$message.success(this.$t('auth.logoutOk'))
        if (this.$route.meta && this.$route.meta.hideNav) {
          this.$router.push('/home')
        }
      }
    }
  }
}
</script>

<style>
html,
body {
  height: auto;
  min-height: 100%;
  overflow-x: hidden;
}

body {
  margin: 0;
  background: #ffffff;
}

.app {
  min-height: 100vh;
  color: #1f2937;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, 'PingFang SC',
    'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.layout {
  min-height: 100vh;
  overflow: visible;
  flex-direction: column;
}

.layout > .el-main {
  flex: 1;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.08);
}

.topbar__inner {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0 18px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
}

.brand__logo {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.brand__name {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: #0f172a;
  white-space: nowrap;
}

.nav {
  flex: 1;
  display: flex;
  justify-content: center;
  border-bottom: none !important;
}

.nav.el-menu--horizontal > .el-menu-item {
  height: 64px;
  line-height: 64px;
  border-bottom: none !important;
  background: transparent !important;
  position: relative;
  margin: 0 4px;
  padding: 0 12px;
  border-radius: 10px;
  transition: color 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.nav.el-menu--horizontal > .el-menu-item::after {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, #2f6bff 0%, #00a3ff 100%);
  transform: scaleX(0);
  transform-origin: center;
  opacity: 0;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 220ms ease;
}

.nav.el-menu--horizontal > .el-menu-item.is-active {
  color: #1f4fff !important;
  font-weight: 600;
}

.nav.el-menu--horizontal > .el-menu-item.is-active::after {
  transform: scaleX(1);
  opacity: 1;
}

.nav.el-menu--horizontal > .el-menu-item:not(.is-disabled):hover {
  color: #0f172a !important;
}

.nav.el-menu--horizontal > .el-menu-item:not(.is-disabled):hover::after {
  transform: scaleX(0.7);
  opacity: 0.7;
}

.nav.el-menu--horizontal > .el-menu-item:focus:not(.is-active) {
  color: #0f172a !important;
}

.nav.el-menu--horizontal > .el-menu-item.is-active:focus {
  color: #1f4fff !important;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-width: 260px;
  margin-right: 18px;
}

.walletRow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  max-width: min(100%, 320px);
}

.wallet-recharge.el-button--primary {
  flex-shrink: 0;
}

.wallet {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(16, 185, 129, 0.28);
  background: rgba(16, 185, 129, 0.08);
  max-width: 200px;
}

.wallet__label {
  font-size: 12px;
  color: rgba(15, 23, 42, 0.62);
  white-space: nowrap;
}

.wallet__value {
  font-size: 14px;
  font-weight: 800;
  color: #047857;
  white-space: nowrap;
}

.user-btn {
  max-width: 140px;
}

.user-btn :deep(span) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lang {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 10px;
  border-radius: 10px;
  cursor: pointer;
  user-select: none;
  color: rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
}

.main {
  width: 100%;
  max-width: none;
  margin: 0;
  box-sizing: border-box;
  overflow: visible;
}

.main.main--auth {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}

@media (max-width: 980px) {
  .nav {
    display: none;
  }

  .brand {
    min-width: auto;
  }

  .actions {
    min-width: auto;
  }
}
</style>

<style>
:root {
  --zj-primary: #2f6bff;
  --zj-primary-soft: #1f4fff;
  --zj-bg: #ffffff;
  --zj-surface: #ffffff;
  --zj-mist: #f6f7f8;
  --zj-text: #17191c;
  --zj-muted: #70777f;
  --zj-line: #e8eaec;
  --zj-shadow: 0 18px 50px rgba(17, 24, 39, 0.06);
}

body {
  background: var(--zj-bg);
}

.app {
  color: var(--zj-text);
  background: var(--zj-bg);
}

.topbar {
  height: 76px !important;
  padding: 0;
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid rgba(23, 25, 28, 0.08);
  box-shadow: none;
  backdrop-filter: blur(18px);
}

.topbar__inner {
  width: min(100%, 1240px);
  max-width: none;
  height: 76px;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;
}

.brand {
  min-width: 220px;
  gap: 11px;
  cursor: pointer;
}

.brand__logo {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.brand__name {
  color: var(--zj-text);
  font-size: 14px;
  letter-spacing: 0.01em;
}

.brand__tagline {
  margin-top: 2px;
  color: #82959a;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.nav.el-menu--horizontal > .el-menu-item {
  height: 76px;
  line-height: 76px;
  margin: 0 1px;
  padding: 0 13px;
  border-radius: 0;
  color: #73787e !important;
  font-size: 13px;
}

.nav.el-menu--horizontal > .el-menu-item::after {
  right: 11px;
  bottom: 18px;
  left: 11px;
  height: 1px;
  background: var(--zj-primary);
}

.nav.el-menu--horizontal > .el-menu-item.is-active {
  color: var(--zj-primary) !important;
}

.actions {
  min-width: 250px;
  margin-right: 0;
}

.lang,
.wallet {
  border-color: var(--zj-line);
  background: #ffffff;
}

.lang {
  height: 34px;
  border-radius: 9px;
  color: #526b72;
  font-size: 12px;
}

.wallet {
  border-radius: 9px;
}

.wallet__value {
  color: var(--zj-primary-soft);
}

.main {
  width: min(100%, 1240px);
  max-width: none;
  margin: 0 auto;
}

.main.main--auth {
  max-width: none;
}

.el-input__inner,
.el-textarea__inner,
.el-select .el-input__inner {
  border-color: var(--zj-line);
  border-radius: 9px;
}

.el-input__inner:focus,
.el-textarea__inner:focus,
.el-select .el-input.is-focus .el-input__inner {
  border-color: var(--zj-primary-soft);
  box-shadow: 0 0 0 3px rgba(45, 106, 120, 0.09);
}

.el-dialog {
  overflow: hidden;
  border: 1px solid var(--zj-line);
  border-radius: 18px;
  box-shadow: 0 28px 80px rgba(16, 42, 50, 0.18);
}

.el-dialog__header {
  padding: 22px 24px 16px;
  border-bottom: 1px solid #e7eef0;
}

.el-dialog__title {
  color: var(--zj-text);
  font-size: 17px;
  font-weight: 750;
}

.el-dialog__body {
  padding: 22px 24px 24px;
}

@media (max-width: 1100px) {
  .brand { min-width: auto; }
  .brand__tagline { display: none; }
  .actions { min-width: auto; }
}

@media (max-width: 720px) {
  .topbar,
  .topbar__inner { height: 64px !important; }
  .topbar__inner { padding: 0 16px; }
  .brand__name { font-size: 14px; }
  .lang { display: none; }
  .actions { gap: 6px; }
  .actions .el-button { margin-left: 0; padding-right: 11px; padding-left: 11px; }
  .main { width: 100%; padding: 38px 16px 60px; }
}

.site-footer {
  padding: 20px 16px 28px;
  background: transparent;
}

.site-footer__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(100%, 1240px);
  margin: 0 auto;
}

.site-footer__beian {
  color: var(--zj-muted);
  font-size: 12px;
  line-height: 1.5;
  text-decoration: none;
  letter-spacing: 0.02em;
  transition: color 180ms ease;
}

.site-footer__beian:hover {
  color: var(--zj-primary);
}
</style>
